import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calculator, Package, Layers, Settings, AlertTriangle, 
  CheckCircle, Info, Download, Eye, Maximize2, HelpCircle,
  Weight, Ruler, Shield, Zap, TrendingUp, DollarSign,
  ChevronRight, ChevronDown, BookOpen, Cpu, FileText,
  Database, Wrench, BarChart3, Lightbulb
} from 'lucide-react';

// Debug logging
import { logModuleInit, invariantArray, safeLength } from '@/utils/invariant';
logModuleInit('TechnicalCalculator.tsx');

// Import nowych modułów - upewnijmy się że są poprawne
import { EnhancedStrengthCalculator } from './EnhancedStrengthCalculator';
import { BeamCalculator, BeamCalculationInput, BeamProfile, STANDARD_PROFILES } from './BeamCalculator';
import { EXTENDED_MATERIALS, STANDARD_THICKNESSES, SAFETY_FACTORS, getMaterialsByCategory } from './ExtendedMaterialDatabase';
import { strengthReportGenerator } from './StrengthReportPDF';
import { beamReportGenerator } from './BeamReportPDF';
import { StrengthVisualization3D } from './StrengthVisualization3D';
import { ShelfSupportCalculator, ShelfSystemInput } from './ShelfSupportCalculator';
import { ShelfSystemVisualization3D } from './ShelfSystemVisualization3D';
import { shelfSystemReportGenerator } from './ShelfSystemReportPDF';

// Import starych modułów jako fallback
import { StrengthCalculator } from './StrengthCalculator';
import { MATERIALS } from './MaterialDatabase';

// Debug: Log what we imported
console.log('[TechnicalCalculator] Imports check:', {
  EnhancedStrengthCalculator: typeof EnhancedStrengthCalculator,
  BeamCalculator: typeof BeamCalculator,
  STANDARD_PROFILES: Array.isArray(STANDARD_PROFILES) ? `Array(${STANDARD_PROFILES.length})` : typeof STANDARD_PROFILES,
  EXTENDED_MATERIALS: typeof EXTENDED_MATERIALS,
  STANDARD_THICKNESSES: typeof STANDARD_THICKNESSES,
  MATERIALS: typeof MATERIALS
});

interface TabProps {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}

const Tab: React.FC<TabProps> = ({ label, icon, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
      active 
        ? 'bg-orange-500 text-white shadow-lg' 
        : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
    }`}
  >
    {icon}
    <span className="font-medium">{label}</span>
  </button>
);

// Feature flags z .env
const DISABLE_3D = import.meta.env.VITE_DISABLE_3D === 'true';
const DISABLE_ENHANCED = import.meta.env.VITE_DISABLE_ENHANCED === 'true';
const DISABLE_BEAM = import.meta.env.VITE_DISABLE_BEAM === 'true';
const DEBUG_MODE = import.meta.env.VITE_DEBUG_MODE === 'true';

if (DEBUG_MODE) {
  console.log('[TechnicalCalculator] Feature flags:', {
    DISABLE_3D,
    DISABLE_ENHANCED,
    DISABLE_BEAM,
    DEBUG_MODE
  });
}

// Wewnętrzny komponent bez ErrorBoundary
const TechnicalCalculatorInner: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'plate' | 'beam' | 'shelf' | 'database' | 'help'>('plate');
  const [showVisualization, setShowVisualization] = useState(!DISABLE_3D); // Domyślnie wyłączone jeśli flaga
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [beamProfileType, setBeamProfileType] = useState<'rod' | 'tube' | 'rectangular' | 'square_tube'>('rod');
  
  // Użyj MATERIALS jeśli EXTENDED_MATERIALS nie jest dostępny
  const materialsDatabase = EXTENDED_MATERIALS || MATERIALS || {};
  const materialKeys = materialsDatabase && typeof materialsDatabase === 'object' ? Object.keys(materialsDatabase) : [];
  const defaultMaterial = materialKeys.length > 0 ? materialKeys[0] : 'pmma_xt';
  
  // Stan dla kalkulatora płyt
  const [plateInput, setPlateInput] = useState({
    material: defaultMaterial,
    thickness: 10,
    width: 800,
    height: 500,
    load: 50,
    loadType: 'uniform' as 'uniform' | 'point',
    supportType: 'four_sides' as any,
    environmentType: 'indoor' as 'indoor' | 'outdoor' | 'industrial',
    temperature: 20,
    safetyFactor: 3,
    projectName: 'Półka magazynowa',
    clientName: 'Klient'
  });

  // Stan dla kalkulatora belek
  const [beamInput, setBeamInput] = useState<BeamCalculationInput | null>(null);
  
  // Stan dla systemu półkowego
  const [shelfInput, setShelfInput] = useState<ShelfSystemInput>({
    shelfWidth: 1000,
    shelfDepth: 400,
    shelfThickness: 10,
    shelfMaterial: defaultMaterial,
    uniformLoad: 50,
    supportHeight: 800,
    supportType: 'both',
    safetyFactor: 3,
    numberOfShelves: 1,
    cornerSupportsRequired: true,
    edgeSupportsRequired: false,
    maxSupportDiameter: 50,
    preferredSupportCount: 4
  });
  const [shelfResult, setShelfResult] = useState<any>(null);
  
  const [optimalSolutions, setOptimalSolutions] = useState<any[]>([]);
  const [beamResult, setBeamResult] = useState<any>(null);

  // Inicjalizacja beamInput
  useEffect(() => {
    try {
      if (STANDARD_PROFILES && Array.isArray(STANDARD_PROFILES) && STANDARD_PROFILES.length > 0) {
        setBeamInput({
          profile: STANDARD_PROFILES[0],
          length: 1000,
          supports: [
            { type: 'fixed', position: 0, allowedRotation: false, allowedTranslationX: false, allowedTranslationY: false },
            { type: 'pinned', position: 1000, allowedRotation: true, allowedTranslationX: false, allowedTranslationY: false }
          ],
          loads: [
            {
              type: 'uniform',
              value: 10,
              direction: 'vertical',
              startPos: 0,
              endPos: 1000
            }
          ],
          considerBuckling: false,
          considerTorsion: false,
          considerDynamicLoads: false,
          safetyFactor: 3
        });
      }
    } catch (error) {
      console.error('Error initializing beam input:', error);
    }
  }, []);

  // Oblicz wytrzymałość przy zmianie parametrów
  useEffect(() => {
    if (activeTab === 'plate') {
      calculatePlateStrength();
    } else if (activeTab === 'beam' && beamInput) {
      calculateBeamStrength();
    } else if (activeTab === 'shelf') {
      calculateShelfSystem();
    }
  }, [plateInput, beamInput, activeTab]);

  const calculatePlateStrength = async () => {
    setIsCalculating(true);
    setError(null);
    try {
      // Sprawdź czy użyć Enhanced czy Basic
      if (!DISABLE_ENHANCED && EnhancedStrengthCalculator && EnhancedStrengthCalculator.findOptimalConfiguration) {
        if (DEBUG_MODE) console.log('[calculatePlateStrength] Using EnhancedStrengthCalculator');
        const solutions = EnhancedStrengthCalculator.findOptimalConfiguration(
          { width: plateInput.width, length: plateInput.height },
          plateInput.load,
          {
            maxDeflection: plateInput.height / 250,
            preferredMaterials: [plateInput.material],
            supportType: plateInput.supportType,
            loadType: plateInput.loadType,
            environment: plateInput.environmentType
          }
        );
        
        if (solutions && Array.isArray(solutions)) {
          setOptimalSolutions(solutions);
        } else {
          // Fallback do prostego kalkulatora
          useFallbackCalculator();
        }
      } else {
        // Fallback do prostego kalkulatora
        useFallbackCalculator();
      }
    } catch (error) {
      console.error('Błąd obliczeń płyty:', error);
      setError('Błąd podczas obliczeń. Używam uproszczonego kalkulatora.');
      useFallbackCalculator();
    } finally {
      setIsCalculating(false);
    }
  };

  const useFallbackCalculator = () => {
    try {
      // Użyj starego StrengthCalculator jako fallback
      if (StrengthCalculator && StrengthCalculator.calculatePlateStrength) {
        const result = StrengthCalculator.calculatePlateStrength({
          material: plateInput.material,
          thickness: plateInput.thickness,
          width: plateInput.width,
          length: plateInput.height,
          supportType: plateInput.supportType,
          loadType: plateInput.loadType,
          load: plateInput.load,
          safetyFactor: plateInput.safetyFactor
        });
        
        // Konwertuj wynik do formatu OptimalSolution
        const fallbackSolution = {
          material: plateInput.material,
          thickness: plateInput.thickness,
          needsSupport: result?.maxDeflection > plateInput.height / 250,
          needsReinforcement: result?.percentOfMaxStress > 70,
          totalCost: 0,
          weight: 0,
          maxDeflection: result?.maxDeflection || 0,
          maxStress: result?.maxStress || 0,
          safetyMargin: result?.percentOfMaxStress ? 100 - result.percentOfMaxStress : 100,
          lifeExpectancy: 10,
          confidenceLevel: 85,
          recommendations: result?.recommendations || []
        };
        
        setOptimalSolutions([fallbackSolution]);
      } else {
        // Jeśli nic nie działa, ustaw pustą listę
        setOptimalSolutions([]);
      }
    } catch (error) {
      console.error('Błąd fallback kalkulatora:', error);
      setOptimalSolutions([]);
    }
  };

  const calculateBeamStrength = async () => {
    if (!beamInput) return;
    
    setIsCalculating(true);
    setError(null);
    try {
      if (BeamCalculator && BeamCalculator.calculate) {
        const result = BeamCalculator.calculate(beamInput);
        setBeamResult(result);
      }
    } catch (error) {
      console.error('Błąd obliczeń belki:', error);
      setError('Błąd podczas obliczeń belki.');
      setBeamResult(null);
    } finally {
      setIsCalculating(false);
    }
  };

  const calculateShelfSystem = async () => {
    setIsCalculating(true);
    setError(null);
    try {
      const material = materialsDatabase[shelfInput.shelfMaterial];
      if (material) {
        const calculator = new ShelfSupportCalculator(material);
        const result = calculator.calculate(shelfInput);
        setShelfResult(result);
      }
    } catch (error) {
      console.error('Błąd obliczeń systemu półkowego:', error);
      setError('Błąd podczas obliczeń systemu półkowego.');
      setShelfResult(null);
    } finally {
      setIsCalculating(false);
    }
  };

  const handlePlateInputChange = (field: string, value: any) => {
    setPlateInput(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBeamInputChange = (field: string, value: any) => {
    if (!beamInput) return;
    setBeamInput(prev => prev ? {
      ...prev,
      [field]: value
    } : null);
  };

  const generatePDFReport = () => {
    if (!optimalSolutions || optimalSolutions.length === 0) return;

    try {
      const reportData = {
        input: plateInput,
        result: {
          maxDeflection: optimalSolutions[0].maxDeflection,
          maxStress: optimalSolutions[0].maxStress,
          safetyMargin: optimalSolutions[0].safetyMargin,
          passed: optimalSolutions[0].safetyMargin > 0,
          criticalPoints: optimalSolutions[0].criticalPoints || [],
          recommendations: optimalSolutions[0].recommendations || []
        },
        optimalSolutions: optimalSolutions,
        date: new Date(),
        projectName: plateInput.projectName,
        clientName: plateInput.clientName
      };

      if (strengthReportGenerator && strengthReportGenerator.generateReport) {
        strengthReportGenerator.generateReport(reportData);
      } else {
        alert('Generator raportów PDF jest niedostępny');
      }
    } catch (error) {
      console.error('Błąd generowania PDF:', error);
      alert('Nie udało się wygenerować raportu PDF');
    }
  };

  const exportResults = () => {
    const report = {
      timestamp: new Date().toISOString(),
      input: activeTab === 'plate' ? plateInput : beamInput,
      results: activeTab === 'plate' ? optimalSolutions : beamResult,
      type: activeTab
    };
    
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wytrzymalosc_${activeTab}_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Funkcja pomocnicza do pobierania materiałów według kategorii
  const getMaterialsByCategoryLocal = (category: string) => {
    try {
      if (getMaterialsByCategory && typeof getMaterialsByCategory === 'function') {
        const result = getMaterialsByCategory(category);
        if (result && Array.isArray(result)) {
          return result;
        }
      }
    } catch (error) {
      console.error('Error in getMaterialsByCategory:', error);
    }
    // Fallback
    if (materialsDatabase && typeof materialsDatabase === 'object') {
      return Object.entries(materialsDatabase)
        .filter(([_, mat]) => mat && (mat as any).category === category)
        .map(([_, mat]) => mat);
    }
    return [];
  };

  return (
    <div className="min-h-screen bg-zinc-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Cpu className="w-8 h-8 text-orange-500" />
            Kalkulator Wytrzymałości Materiałów v2.0
          </h1>
          <p className="text-gray-400 mt-2">
            Profesjonalne obliczenia wytrzymałościowe z bazą materiałów i generowaniem raportów
          </p>
        </motion.div>

        {/* Error Alert */}
        {error && (
          <div className="mb-4 p-4 bg-red-900/20 border border-red-500 rounded-lg text-red-300">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <Tab
            label="Płyty i panele"
            icon={<Layers className="w-4 h-4" />}
            active={activeTab === 'plate'}
            onClick={() => setActiveTab('plate')}
          />
          <Tab
            label="Belki i profile"
            icon={<Package className="w-4 h-4" />}
            active={activeTab === 'beam'}
            onClick={() => setActiveTab('beam')}
          />
          <Tab
            label="System półkowy"
            icon={<Layers className="w-4 h-4" />}
            active={activeTab === 'shelf'}
            onClick={() => setActiveTab('shelf')}
          />
          <Tab
            label="Baza materiałów"
            icon={<Database className="w-4 h-4" />}
            active={activeTab === 'database'}
            onClick={() => setActiveTab('database')}
          />
          <Tab
            label="Pomoc"
            icon={<HelpCircle className="w-4 h-4" />}
            active={activeTab === 'help'}
            onClick={() => setActiveTab('help')}
          />
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Input */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="bg-zinc-800 rounded-xl p-6 shadow-xl">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-orange-500" />
                Parametry obliczeniowe
              </h2>

              {activeTab === 'plate' && (
                <div className="space-y-4">
                  {/* Nazwa projektu */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Nazwa projektu
                    </label>
                    <input
                      type="text"
                      value={plateInput.projectName}
                      onChange={(e) => handlePlateInputChange('projectName', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                    />
                  </div>

                  {/* Materiał */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Materiał
                    </label>
                    <select
                      value={plateInput.material}
                      onChange={(e) => handlePlateInputChange('material', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                    >
                      {materialsDatabase && typeof materialsDatabase === 'object' && Object.entries(materialsDatabase).map(([key, mat]) => (
                        mat ? (
                          <option key={key} value={key}>
                            {(mat as any).displayName || key}
                          </option>
                        ) : null
                      ))}
                    </select>
                  </div>

                  {/* Grubość */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Grubość (mm)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={plateInput.thickness}
                        onChange={(e) => handlePlateInputChange('thickness', Number(e.target.value))}
                        className="flex-1 px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                        min="1"
                        max="100"
                        step="1"
                      />
                      {STANDARD_THICKNESSES && STANDARD_THICKNESSES[plateInput.material] && Array.isArray(STANDARD_THICKNESSES[plateInput.material]) && STANDARD_THICKNESSES[plateInput.material].length > 0 && (
                        <select
                          value={plateInput.thickness}
                          onChange={(e) => handlePlateInputChange('thickness', Number(e.target.value))}
                          className="px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                        >
                          {STANDARD_THICKNESSES[plateInput.material].map((t: number) => (
                            <option key={t} value={t}>{t}mm</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>

                  {/* Wymiary */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Szerokość (mm)
                      </label>
                      <input
                        type="number"
                        value={plateInput.width}
                        onChange={(e) => handlePlateInputChange('width', Number(e.target.value))}
                        className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                        min="10"
                        max="5000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Długość (mm)
                      </label>
                      <input
                        type="number"
                        value={plateInput.height}
                        onChange={(e) => handlePlateInputChange('height', Number(e.target.value))}
                        className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                        min="10"
                        max="5000"
                      />
                    </div>
                  </div>

                  {/* Typ podparcia */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Sposób podparcia
                    </label>
                    <select
                      value={plateInput.supportType}
                      onChange={(e) => handlePlateInputChange('supportType', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="simply_supported">Podparte na 2 końcach</option>
                      <option value="cantilever">Wspornik (zamocowane z jednej strony)</option>
                      <option value="fixed_both">Utwierdzone na obu końcach</option>
                      <option value="three_sides">Podparte na 3 bokach</option>
                      <option value="four_sides">Podparte na 4 bokach</option>
                      <option value="continuous">Ciągłe podparcie</option>
                      <option value="point_supports">Podpory punktowe</option>
                    </select>
                  </div>

                  {/* Typ obciążenia */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Typ obciążenia
                    </label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handlePlateInputChange('loadType', 'uniform')}
                        className={`flex-1 px-3 py-2 rounded-lg transition-all ${
                          plateInput.loadType === 'uniform'
                            ? 'bg-orange-500 text-white'
                            : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                        }`}
                      >
                        Równomierne
                      </button>
                      <button
                        onClick={() => handlePlateInputChange('loadType', 'point')}
                        className={`flex-1 px-3 py-2 rounded-lg transition-all ${
                          plateInput.loadType === 'point'
                            ? 'bg-orange-500 text-white'
                            : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                        }`}
                      >
                        Punktowe
                      </button>
                    </div>
                  </div>

                  {/* Obciążenie */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Obciążenie {plateInput.loadType === 'uniform' ? '(kg/m²)' : '(kg)'}
                    </label>
                    <input
                      type="number"
                      value={plateInput.load}
                      onChange={(e) => handlePlateInputChange('load', Number(e.target.value))}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                      min="0"
                      max="10000"
                      step="10"
                    />
                  </div>

                  {/* Środowisko */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Środowisko pracy
                    </label>
                    <select
                      value={plateInput.environmentType}
                      onChange={(e) => handlePlateInputChange('environmentType', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="indoor">Wewnętrzne</option>
                      <option value="outdoor">Zewnętrzne</option>
                      <option value="industrial">Przemysłowe</option>
                    </select>
                  </div>

                  {/* Współczynnik bezpieczeństwa */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Współczynnik bezpieczeństwa
                    </label>
                    <select
                      value={plateInput.safetyFactor}
                      onChange={(e) => handlePlateInputChange('safetyFactor', Number(e.target.value))}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                    >
                      <option value={2}>Ekspozycja (2.0)</option>
                      <option value={3}>Magazynowanie (3.0)</option>
                      <option value={4}>Konstrukcyjne (4.0)</option>
                      <option value={5}>Przestrzeń publiczna (5.0)</option>
                      <option value={6}>Przemysłowe (6.0)</option>
                      <option value={10}>Krytyczne bezpieczeństwo (10.0)</option>
                    </select>
                  </div>
                </div>
              )}

              {activeTab === 'shelf' && (
                <div className="space-y-4">
                  {/* Wymiary półki */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Wymiary półki
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <input
                          type="number"
                          value={shelfInput.shelfWidth}
                          onChange={(e) => setShelfInput(prev => ({...prev, shelfWidth: Number(e.target.value)}))}
                          className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                          placeholder="Szerokość"
                          min="100"
                          max="5000"
                        />
                        <span className="text-xs text-gray-400 mt-1 block">Szerokość (mm)</span>
                      </div>
                      <div>
                        <input
                          type="number"
                          value={shelfInput.shelfDepth}
                          onChange={(e) => setShelfInput(prev => ({...prev, shelfDepth: Number(e.target.value)}))}
                          className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                          placeholder="Głębokość"
                          min="100"
                          max="2000"
                        />
                        <span className="text-xs text-gray-400 mt-1 block">Głębokość (mm)</span>
                      </div>
                      <div>
                        <input
                          type="number"
                          value={shelfInput.shelfThickness}
                          onChange={(e) => setShelfInput(prev => ({...prev, shelfThickness: Number(e.target.value)}))}
                          className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                          placeholder="Grubość"
                          min="3"
                          max="50"
                        />
                        <span className="text-xs text-gray-400 mt-1 block">Grubość (mm)</span>
                      </div>
                    </div>
                  </div>

                  {/* Materiał półki */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Materiał półki
                    </label>
                    <select
                      value={shelfInput.shelfMaterial}
                      onChange={(e) => setShelfInput(prev => ({...prev, shelfMaterial: e.target.value}))}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                    >
                      {materialsDatabase && typeof materialsDatabase === 'object' && Object.entries(materialsDatabase).map(([key, mat]) => (
                        mat ? (
                          <option key={key} value={key}>
                            {(mat as any).displayName || key}
                          </option>
                        ) : null
                      ))}
                    </select>
                  </div>

                  {/* Obciążenie */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Obciążenie równomierne (kg/m²)
                    </label>
                    <input
                      type="number"
                      value={shelfInput.uniformLoad}
                      onChange={(e) => setShelfInput(prev => ({...prev, uniformLoad: Number(e.target.value)}))}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                      min="0"
                      max="1000"
                      step="10"
                    />
                  </div>

                  {/* Wysokość systemu */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Wysokość słupków (mm)
                    </label>
                    <input
                      type="number"
                      value={shelfInput.supportHeight}
                      onChange={(e) => setShelfInput(prev => ({...prev, supportHeight: Number(e.target.value)}))}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                      min="100"
                      max="3000"
                      step="50"
                    />
                  </div>

                  {/* Liczba półek */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Liczba półek w systemie
                    </label>
                    <input
                      type="number"
                      value={shelfInput.numberOfShelves}
                      onChange={(e) => setShelfInput(prev => ({...prev, numberOfShelves: Number(e.target.value)}))}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                      min="1"
                      max="10"
                    />
                  </div>

                  {/* Typ podpór */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Typ podpór do rozważenia
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => setShelfInput(prev => ({...prev, supportType: 'rod'}))}
                        className={`px-3 py-2 rounded-lg transition-all ${
                          shelfInput.supportType === 'rod'
                            ? 'bg-orange-500 text-white'
                            : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                        }`}
                      >
                        🔵 Pręt
                      </button>
                      <button
                        onClick={() => setShelfInput(prev => ({...prev, supportType: 'tube'}))}
                        className={`px-3 py-2 rounded-lg transition-all ${
                          shelfInput.supportType === 'tube'
                            ? 'bg-orange-500 text-white'
                            : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                        }`}
                      >
                        ⭕ Rurka
                      </button>
                      <button
                        onClick={() => setShelfInput(prev => ({...prev, supportType: 'both'}))}
                        className={`px-3 py-2 rounded-lg transition-all ${
                          shelfInput.supportType === 'both'
                            ? 'bg-orange-500 text-white'
                            : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                        }`}
                      >
                        🔄 Oba
                      </button>
                    </div>
                  </div>

                  {/* Preferowana liczba podpór */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Preferowana liczba podpór
                    </label>
                    <select
                      value={shelfInput.preferredSupportCount}
                      onChange={(e) => setShelfInput(prev => ({...prev, preferredSupportCount: Number(e.target.value)}))}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                    >
                      <option value={4}>4 (narożniki)</option>
                      <option value={6}>6 (narożniki + środek)</option>
                      <option value={8}>8 (narożniki + boki)</option>
                      <option value={9}>9 (siatka 3x3)</option>
                      <option value={12}>12 (siatka 3x4)</option>
                      <option value={16}>16 (siatka 4x4)</option>
                    </select>
                  </div>

                  {/* Współczynnik bezpieczeństwa */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Współczynnik bezpieczeństwa
                    </label>
                    <select
                      value={shelfInput.safetyFactor}
                      onChange={(e) => setShelfInput(prev => ({...prev, safetyFactor: Number(e.target.value)}))}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                    >
                      <option value={2}>2.0 (Ekspozycja)</option>
                      <option value={3}>3.0 (Magazynowanie)</option>
                      <option value={4}>4.0 (Konstrukcyjne)</option>
                      <option value={5}>5.0 (Przestrzeń publiczna)</option>
                    </select>
                  </div>

                  {/* Opcje dodatkowe */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={shelfInput.cornerSupportsRequired}
                        onChange={(e) => setShelfInput(prev => ({...prev, cornerSupportsRequired: e.target.checked}))}
                        className="rounded border-zinc-600 bg-zinc-700 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="text-sm text-gray-300">Wymagane podpory w narożach</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={shelfInput.edgeSupportsRequired || false}
                        onChange={(e) => setShelfInput(prev => ({...prev, edgeSupportsRequired: e.target.checked}))}
                        className="rounded border-zinc-600 bg-zinc-700 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="text-sm text-gray-300">Wymagane podpory na krawędziach</span>
                    </label>
                  </div>
                </div>
              )}

              {activeTab === 'beam' && (
                <div className="space-y-4">
                  {beamInput && STANDARD_PROFILES && Array.isArray(STANDARD_PROFILES) && STANDARD_PROFILES.length > 0 ? (
                    <>
                      {/* Typ profilu */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Typ profilu
                        </label>
                        <div className="grid grid-cols-2 gap-2 mb-4">
                          <button
                            onClick={() => setBeamProfileType('rod')}
                            className={`px-3 py-2 rounded-lg transition-all ${
                              beamProfileType === 'rod'
                                ? 'bg-orange-500 text-white'
                                : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                            }`}
                          >
                            🔵 Pręt
                          </button>
                          <button
                            onClick={() => setBeamProfileType('tube')}
                            className={`px-3 py-2 rounded-lg transition-all ${
                              beamProfileType === 'tube'
                                ? 'bg-orange-500 text-white'
                                : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                            }`}
                          >
                            ⭕ Rurka
                          </button>
                          <button
                            onClick={() => setBeamProfileType('rectangular')}
                            className={`px-3 py-2 rounded-lg transition-all ${
                              beamProfileType === 'rectangular'
                                ? 'bg-orange-500 text-white'
                                : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                            }`}
                          >
                            ▬ Prostokątny
                          </button>
                          <button
                            onClick={() => setBeamProfileType('square_tube')}
                            className={`px-3 py-2 rounded-lg transition-all ${
                              beamProfileType === 'square_tube'
                                ? 'bg-orange-500 text-white'
                                : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                            }`}
                          >
                            ◻ Kwadratowy
                          </button>
                        </div>
                      </div>
                      
                      {/* Profile selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Profil belki
                        </label>
                        <select
                          value={beamInput.profile && STANDARD_PROFILES ? STANDARD_PROFILES.findIndex(p => p && beamInput.profile && p.name === beamInput.profile.name) : 0}
                          onChange={(e) => {
                            const idx = Number(e.target.value);
                            if (STANDARD_PROFILES && STANDARD_PROFILES[idx]) {
                              handleBeamInputChange('profile', STANDARD_PROFILES[idx]);
                            }
                          }}
                          className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                        >
                          {STANDARD_PROFILES && Array.isArray(STANDARD_PROFILES) && 
                           STANDARD_PROFILES
                             .filter(profile => profile && profile.type === beamProfileType)
                             .map((profile, idx) => (
                               profile ? (
                                 <option key={profile.name} value={STANDARD_PROFILES.indexOf(profile)}>
                                   {profile.name || `Profile ${idx}`}
                                 </option>
                               ) : null
                             ))}
                        </select>
                      </div>

                      {/* Material */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Materiał
                        </label>
                        <select
                          value={beamInput.profile?.material || 'pmma_xt'}
                          onChange={(e) => {
                            if (beamInput.profile) {
                              handleBeamInputChange('profile', { ...beamInput.profile, material: e.target.value });
                            }
                          }}
                          className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                        >
                          {materialsDatabase && typeof materialsDatabase === 'object' && Object.entries(materialsDatabase).map(([key, mat]) => (
                            mat ? (
                              <option key={key} value={key}>
                                {(mat as any).displayName || key}
                              </option>
                            ) : null
                          ))}
                        </select>
                      </div>

                      {/* Length */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Długość belki (mm)
                        </label>
                        <input
                          type="number"
                          value={beamInput.length}
                          onChange={(e) => handleBeamInputChange('length', Number(e.target.value))}
                          className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                          min="100"
                          max="10000"
                          step="100"
                        />
                      </div>
                      
                      {/* Typ podparcia */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Sposób podparcia
                        </label>
                        <select
                          value={beamInput.supports[0]?.type || 'simply_supported'}
                          onChange={(e) => {
                            const supportType = e.target.value;
                            let newSupports = [];
                            if (supportType === 'simply_supported') {
                              newSupports = [
                                { type: 'pinned', position: 0, allowedRotation: true, allowedTranslationX: false, allowedTranslationY: false },
                                { type: 'pinned', position: beamInput.length, allowedRotation: true, allowedTranslationX: false, allowedTranslationY: false }
                              ];
                            } else if (supportType === 'cantilever') {
                              newSupports = [
                                { type: 'fixed', position: 0, allowedRotation: false, allowedTranslationX: false, allowedTranslationY: false }
                              ];
                            } else if (supportType === 'fixed_both') {
                              newSupports = [
                                { type: 'fixed', position: 0, allowedRotation: false, allowedTranslationX: false, allowedTranslationY: false },
                                { type: 'fixed', position: beamInput.length, allowedRotation: false, allowedTranslationX: false, allowedTranslationY: false }
                              ];
                            }
                            handleBeamInputChange('supports', newSupports);
                          }}
                          className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                        >
                          <option value="simply_supported">Podparta na dwóch końcach (przegubowo)</option>
                          <option value="cantilever">Wspornik (jednostronnie utwierdzona)</option>
                          <option value="fixed_both">Utwierdzona na obu końcach</option>
                        </select>
                      </div>
                      
                      {/* Obciążenie */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Obciążenie (N/m dla równomiernego, N dla punktowego)
                        </label>
                        <input
                          type="number"
                          value={beamInput.loads[0]?.value || 10}
                          onChange={(e) => {
                            const newLoads = [...beamInput.loads];
                            if (newLoads[0]) {
                              newLoads[0].value = Number(e.target.value);
                              handleBeamInputChange('loads', newLoads);
                            }
                          }}
                          className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                          min="0"
                          max="10000"
                          step="10"
                        />
                      </div>
                      
                      {/* Typ obciążenia */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Typ obciążenia
                        </label>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              const newLoads = [{
                                type: 'uniform' as const,
                                value: beamInput.loads[0]?.value || 10,
                                direction: 'vertical' as const,
                                startPos: 0,
                                endPos: beamInput.length
                              }];
                              handleBeamInputChange('loads', newLoads);
                            }}
                            className={`flex-1 px-3 py-2 rounded-lg transition-all ${
                              beamInput.loads[0]?.type === 'uniform'
                                ? 'bg-orange-500 text-white'
                                : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                            }`}
                          >
                            Równomierne
                          </button>
                          <button
                            onClick={() => {
                              const newLoads = [{
                                type: 'point' as const,
                                value: beamInput.loads[0]?.value || 100,
                                direction: 'vertical' as const,
                                position: beamInput.length / 2
                              }];
                              handleBeamInputChange('loads', newLoads);
                            }}
                            className={`flex-1 px-3 py-2 rounded-lg transition-all ${
                              beamInput.loads[0]?.type === 'point'
                                ? 'bg-orange-500 text-white'
                                : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                            }`}
                          >
                            Punktowe
                          </button>
                        </div>
                      </div>
                      
                      {/* Dodatkowe opcje */}
                      <div className="space-y-2">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={beamInput.considerBuckling}
                            onChange={(e) => handleBeamInputChange('considerBuckling', e.target.checked)}
                            className="rounded border-zinc-600 bg-zinc-700 text-orange-500 focus:ring-orange-500"
                          />
                          <span className="text-sm text-gray-300">Sprawdź wyboczenie</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={beamInput.considerTorsion}
                            onChange={(e) => handleBeamInputChange('considerTorsion', e.target.checked)}
                            className="rounded border-zinc-600 bg-zinc-700 text-orange-500 focus:ring-orange-500"
                          />
                          <span className="text-sm text-gray-300">Sprawdź skręcanie</span>
                        </label>
                      </div>
                    </>
                  ) : (
                    <div className="text-gray-400 text-center py-8">
                      <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Kalkulator belek w przygotowaniu</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>

          {/* Right Panel - Results & Visualization */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2"
          >
            {/* Results for Beams */}
            {activeTab === 'beam' && beamResult && (
              <div className="bg-zinc-800 rounded-xl p-6 shadow-xl mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-orange-500" />
                    Wyniki analizy belki
                  </h2>
                  <button
                    onClick={() => {
                    if (beamInput && beamResult) {
                    const material = beamInput.profile?.material ? materialsDatabase[beamInput.profile.material] : null;
                    if (material) {
                      beamReportGenerator.generateReport(beamInput, beamResult, material);
                        beamReportGenerator.save(`belka_${new Date().getTime()}.pdf`);
                        } else {
            alert('Błąd: Brak danych materiału dla belki');
          }
        }
      }}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Pobierz PDF
                  </button>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-zinc-700 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-1">Max ugięcie</p>
                    <p className="text-xl font-bold text-white">
                      {beamResult.maxDeflection?.toFixed(2) || '0.00'} mm
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      @ {beamResult.maxDeflectionPosition?.toFixed(0) || '0'} mm
                    </p>
                  </div>
                  
                  <div className="bg-zinc-700 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-1">Max naprężenie</p>
                    <p className="text-xl font-bold text-white">
                      {beamResult.maxBendingStress?.toFixed(1) || '0.0'} MPa
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      von Mises: {beamResult.vonMisesStress?.toFixed(1) || '0.0'} MPa
                    </p>
                  </div>
                  
                  <div className="bg-zinc-700 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-1">Wykorzystanie</p>
                    <p className="text-xl font-bold text-white">
                      {beamResult.utilizationRatio?.toFixed(0) || '0'}%
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {beamResult.criticalMode || 'bending'}
                    </p>
                  </div>
                  
                  <div className="bg-zinc-700 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-1">Status</p>
                    <p className="text-xl font-bold text-white">
                      {beamResult.isSafe ? (
                        <span className="text-green-500">BEZPIECZNE</span>
                      ) : (
                        <span className="text-red-500">NIEBEZPIECZNE</span>
                      )}
                    </p>
                    {beamResult.bucklingFactor && (
                      <p className="text-xs text-gray-500 mt-1">
                        Wyboczenie: {beamResult.bucklingFactor.toFixed(1)}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Rekomendacje */}
                {beamResult.recommendations && beamResult.recommendations.length > 0 && (
                  <div className="mt-4 p-4 bg-zinc-700 rounded-lg">
                    <h3 className="text-sm font-semibold text-white mb-2">Rekomendacje:</h3>
                    <ul className="space-y-1">
                      {beamResult.recommendations.map((rec: string, idx: number) => (
                        <li key={idx} className="text-xs text-gray-300 flex items-start gap-2">
                          <span className="text-yellow-500">•</span>
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            
            {/* Results for Shelf System */}
            {activeTab === 'shelf' && shelfResult && (
              <div className="bg-zinc-800 rounded-xl p-6 shadow-xl mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                    <Package className="w-5 h-5 text-orange-500" />
                    Optymalne konfiguracje systemu półkowego
                  </h2>
                  <button
                    onClick={() => {
                      if (shelfResult) {
                        shelfSystemReportGenerator.generateReport(
                          shelfInput,
                          shelfResult,
                          'System półkowy',
                          'Klient'
                        );
                        shelfSystemReportGenerator.save(`system_polkowy_${new Date().getTime()}.pdf`);
                      }
                    }}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Generuj PDF
                  </button>
                </div>

                {/* Optymalne konfiguracje */}
                {shelfResult.optimalConfigurations && shelfResult.optimalConfigurations.length > 0 ? (
                  <div className="space-y-4">
                    {shelfResult.optimalConfigurations.slice(0, 3).map((config: any, idx: number) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className={`p-4 rounded-lg border ${
                          idx === 0
                            ? 'bg-gradient-to-r from-green-900/20 to-green-800/20 border-green-500'
                            : 'bg-zinc-700 border-zinc-600'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            {idx === 0 && (
                              <div className="px-2 py-1 bg-green-500 rounded text-xs font-bold text-white">
                                REKOMENDOWANE
                              </div>
                            )}
                            <h3 className="text-lg font-semibold text-white">
                              {config.type === 'rod' ? '🔵 Pręt' : '⭕ Rurka'} PMMA ø{config.diameter}mm
                              {config.innerDiameter && ` / ø${config.innerDiameter}mm`}
                            </h3>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-orange-500">
                              {config.totalCost.toFixed(0)} PLN
                            </p>
                            <p className="text-xs text-gray-400">szacunkowy koszt</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                          <div className="bg-zinc-800 rounded p-2">
                            <p className="text-xs text-gray-400">Ilość</p>
                            <p className="text-sm font-bold text-white">
                              {config.count} szt.
                            </p>
                          </div>
                          <div className="bg-zinc-800 rounded p-2">
                            <p className="text-xs text-gray-400">Ugięcie</p>
                            <p className="text-sm font-bold text-white">
                              {config.maxShelfDeflection.toFixed(2)} mm
                            </p>
                          </div>
                          <div className="bg-zinc-800 rounded p-2">
                            <p className="text-xs text-gray-400">Naprężenie</p>
                            <p className="text-sm font-bold text-white">
                              {config.maxStressInSupport.toFixed(1)} MPa
                            </p>
                          </div>
                          <div className="bg-zinc-800 rounded p-2">
                            <p className="text-xs text-gray-400">Wyboczenie</p>
                            <p className="text-sm font-bold text-yellow-500">
                              {config.maxBuckling.toFixed(1)}
                            </p>
                          </div>
                          <div className="bg-zinc-800 rounded p-2">
                            <p className="text-xs text-gray-400">Waga</p>
                            <p className="text-sm font-bold text-white">
                              {config.totalWeight.toFixed(1)} kg
                            </p>
                          </div>
                        </div>

                        {/* Status */}
                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {config.isSafe ? (
                              <>
                                <CheckCircle className="w-5 h-5 text-green-500" />
                                <span className="text-green-500 font-semibold">BEZPIECZNA KONFIGURACJA</span>
                              </>
                            ) : (
                              <>
                                <AlertTriangle className="w-5 h-5 text-red-500" />
                                <span className="text-red-500 font-semibold">NIEBEZPIECZNA KONFIGURACJA</span>
                              </>
                            )}
                          </div>
                          <span className="text-sm text-gray-400">
                            Margines: {config.safetyMargin.toFixed(0)}%
                          </span>
                        </div>

                        {/* Rekomendacje */}
                        {config.recommendations && config.recommendations.length > 0 && (
                          <div className="mt-3 space-y-1">
                            {config.recommendations.slice(0, 2).map((rec: string, ridx: number) => (
                              <div key={ridx} className="flex items-start gap-2">
                                <Lightbulb className="w-3 h-3 text-yellow-500 mt-0.5" />
                                <p className="text-xs text-gray-400">{rec}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-gray-300">Nie znaleziono bezpiecznej konfiguracji dla zadanych parametrów.</p>
                    <p className="text-sm text-gray-400 mt-2">Zmniejsz obciążenie lub zwiększ dopuszczalne wymiary podpór.</p>
                  </div>
                )}

                {/* Instrukcja montażu */}
                {shelfResult.installationGuide && shelfResult.installationGuide.length > 0 && (
                  <div className="mt-6 p-4 bg-zinc-700 rounded-lg">
                    <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Wrench className="w-4 h-4 text-orange-500" />
                      Instrukcja montażu
                    </h3>
                    <div className="space-y-2">
                      {shelfResult.installationGuide.slice(0, 5).map((step: string, idx: number) => (
                        <div key={idx} className="text-xs text-gray-300">
                          {step}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 3D Visualization for Shelf System */}
            {activeTab === 'shelf' && shelfResult && shelfResult.optimalConfigurations && shelfResult.optimalConfigurations[0] && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-zinc-800 rounded-xl p-6 shadow-xl"
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                    <Eye className="w-5 h-5 text-orange-500" />
                    Wizualizacja 3D systemu
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowVisualization(!showVisualization)}
                      className="p-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg transition-all"
                      title="Przełącz wizualizację"
                    >
                      <Maximize2 className="w-4 h-4 text-white" />
                    </button>
                  </div>
                </div>
                
                {showVisualization && (
                  <div className="h-96 bg-zinc-900 rounded-lg">
                    <ShelfSystemVisualization3D
                      shelfWidth={shelfInput.shelfWidth}
                      shelfDepth={shelfInput.shelfDepth}
                      shelfThickness={shelfInput.shelfThickness}
                      supportHeight={shelfInput.supportHeight}
                      numberOfShelves={shelfInput.numberOfShelves || 1}
                      shelfSpacing={400}
                      supportConfig={shelfResult.optimalConfigurations[0]}
                      showDeflection={true}
                      showStress={false}
                    />
                  </div>
                )}
              </motion.div>
            )}
            
            {/* Results for Plates */}
            {activeTab === 'plate' && optimalSolutions && Array.isArray(optimalSolutions) && optimalSolutions.length > 0 && (
              <div className="bg-zinc-800 rounded-xl p-6 shadow-xl mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-orange-500" />
                    Wyniki analizy ({optimalSolutions.length})
                  </h2>
                  <div className="flex gap-2">
                    {strengthReportGenerator && (
                      <button
                        onClick={generatePDFReport}
                        className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-all flex items-center gap-2"
                        title="Generuj raport PDF"
                      >
                        <FileText className="w-4 h-4 text-white" />
                        <span className="text-white text-sm">PDF</span>
                      </button>
                    )}
                    <button
                      onClick={exportResults}
                      className="p-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg transition-all"
                      title="Eksportuj JSON"
                    >
                      <Download className="w-4 h-4 text-white" />
                    </button>
                  </div>
                </div>

                {/* Solutions Grid */}
                <div className="space-y-4">
                  {optimalSolutions && Array.isArray(optimalSolutions) && optimalSolutions.slice(0, 3).map((solution, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className={`p-4 rounded-lg border ${
                        idx === 0 
                          ? 'bg-gradient-to-r from-green-900/20 to-green-800/20 border-green-500' 
                          : 'bg-zinc-700 border-zinc-600'
                      }`}
                    >
                      {/* Solution Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {idx === 0 && (
                            <div className="px-2 py-1 bg-green-500 rounded text-xs font-bold text-white">
                              REKOMENDOWANE
                            </div>
                          )}
                          <h3 className="text-lg font-semibold text-white">
                            {materialsDatabase && materialsDatabase[solution.material] ? (materialsDatabase[solution.material] as any).displayName || solution.material : solution.material} {solution.thickness}mm
                          </h3>
                        </div>
                        {solution.totalCost > 0 && (
                          <div className="text-right">
                            <p className="text-2xl font-bold text-orange-500">
                              {solution.totalCost.toFixed(0)} PLN
                            </p>
                            <p className="text-xs text-gray-400">koszt całkowity</p>
                          </div>
                        )}
                      </div>

                      {/* Solution Metrics */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-zinc-800 rounded p-2">
                          <p className="text-xs text-gray-400">Ugięcie</p>
                          <p className="text-sm font-bold text-white">
                            {solution.maxDeflection?.toFixed(2) || '0.00'} mm
                          </p>
                        </div>
                        <div className="bg-zinc-800 rounded p-2">
                          <p className="text-xs text-gray-400">Naprężenie</p>
                          <p className="text-sm font-bold text-white">
                            {solution.maxStress?.toFixed(1) || '0.0'} MPa
                          </p>
                        </div>
                        <div className="bg-zinc-800 rounded p-2">
                          <p className="text-xs text-gray-400">Margines</p>
                          <p className="text-sm font-bold text-green-500">
                            +{solution.safetyMargin?.toFixed(0) || '0'}%
                          </p>
                        </div>
                        {solution.confidenceLevel && (
                          <div className="bg-zinc-800 rounded p-2">
                            <p className="text-xs text-gray-400">Pewność</p>
                            <p className="text-sm font-bold text-blue-500">
                              {solution.confidenceLevel}%
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Support & Reinforcement Info */}
                      {(solution.needsSupport || solution.needsReinforcement) && (
                        <div className="mt-3 pt-3 border-t border-zinc-600">
                          {solution.needsSupport && solution.supportConfig && (
                            <div className="flex items-center gap-2 text-sm">
                              <Wrench className="w-4 h-4 text-yellow-500" />
                              <span className="text-gray-300">
                                Wymaga wsparcia: <strong>{solution.supportConfig.type}</strong>
                              </span>
                            </div>
                          )}
                          {solution.needsReinforcement && solution.reinforcementConfig && (
                            <div className="flex items-center gap-2 text-sm mt-1">
                              <Shield className="w-4 h-4 text-purple-500" />
                              <span className="text-gray-300">
                                Wzmocnienie: <strong>{solution.reinforcementConfig.type}</strong>
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Recommendations */}
                      {solution.recommendations && Array.isArray(solution.recommendations) && solution.recommendations.length > 0 && (
                        <div className="mt-3 space-y-1">
                          {solution.recommendations.slice(0, 2).map((rec: any, ridx: number) => (
                            <div key={ridx} className="flex items-start gap-2">
                              <Lightbulb className="w-3 h-3 text-yellow-500 mt-0.5" />
                              <p className="text-xs text-gray-400">{String(rec)}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Visualization */}
            {showVisualization && optimalSolutions && Array.isArray(optimalSolutions) && optimalSolutions.length > 0 && optimalSolutions[0] && activeTab === 'plate' && StrengthVisualization3D && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-zinc-800 rounded-xl p-6 shadow-xl"
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                    <Eye className="w-5 h-5 text-orange-500" />
                    Wizualizacja 3D
                  </h2>
                  <button
                    onClick={() => setShowVisualization(!showVisualization)}
                    className="p-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg transition-all"
                  >
                    <Maximize2 className="w-4 h-4 text-white" />
                  </button>
                </div>
                
                <div className="h-96 bg-zinc-900 rounded-lg">
                  <StrengthVisualization3D
                    input={{
                      material: optimalSolutions[0].material || plateInput.material,
                      thickness: optimalSolutions[0].thickness || plateInput.thickness,
                      width: plateInput.width,
                      length: plateInput.height,
                      supportType: plateInput.supportType,
                      loadType: plateInput.loadType,
                      load: plateInput.load,
                      safetyFactor: plateInput.safetyFactor
                    }}
                    result={{
                      maxDeflection: optimalSolutions[0].maxDeflection || 0,
                      maxStress: optimalSolutions[0].maxStress || 0,
                      percentOfMaxStress: materialsDatabase && optimalSolutions[0] && materialsDatabase[optimalSolutions[0].material] ? 
                        ((optimalSolutions[0].maxStress || 0) / ((materialsDatabase[optimalSolutions[0].material] as any).flexuralStrength || 100) * 100) : 0,
                      safetyFactor: plateInput.safetyFactor,
                      isSafe: optimalSolutions[0].safetyMargin > 0,
                      recommendations: optimalSolutions[0].recommendations || [],
                      alternativeSolutions: []
                    }}
                  />
                </div>
              </motion.div>
            )}

            {/* Database Tab Content */}
            {activeTab === 'database' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-zinc-800 rounded-xl p-6 shadow-xl lg:col-span-2"
              >
                <h2 className="text-xl font-semibold text-white mb-4">
                  Baza materiałów
                </h2>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-700">
                        <th className="text-left py-2 px-3 text-gray-400">Materiał</th>
                        <th className="text-right py-2 px-3 text-gray-400">Gęstość</th>
                        <th className="text-right py-2 px-3 text-gray-400">E-moduł</th>
                        <th className="text-right py-2 px-3 text-gray-400">Wytrzym.</th>
                        <th className="text-right py-2 px-3 text-gray-400">Temp.</th>
                        <th className="text-right py-2 px-3 text-gray-400">Cena</th>
                      </tr>
                    </thead>
                    <tbody>
                      {materialsDatabase && typeof materialsDatabase === 'object' && Object.entries(materialsDatabase).map(([key, mat]) => (
                        mat ? (
                          <tr key={key} className="border-b border-zinc-700 hover:bg-zinc-700 transition-all">
                            <td className="py-2 px-3 text-white font-medium">
                              {(mat as any).displayName || key}
                            </td>
                            <td className="text-right py-2 px-3 text-gray-300">
                              {(mat as any).density || '-'} kg/m³
                            </td>
                            <td className="text-right py-2 px-3 text-gray-300">
                              {(mat as any).modulusOfElasticity || '-'} MPa
                            </td>
                            <td className="text-right py-2 px-3 text-gray-300">
                              {(mat as any).flexuralStrength || '-'} MPa
                            </td>
                            <td className="text-right py-2 px-3 text-gray-300">
                              {(mat as any).maxServiceTemp || '-'}°C
                            </td>
                            <td className="text-right py-2 px-3 text-gray-300">
                              {(mat as any).pricePerKg || '-'} zł/kg
                            </td>
                          </tr>
                        ) : null
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {/* Help Tab Content */}
            {activeTab === 'help' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-zinc-800 rounded-xl p-6 shadow-xl lg:col-span-2"
              >
                <h2 className="text-xl font-semibold text-white mb-4">Pomoc i dokumentacja</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-orange-500 mb-3">Funkcje kalkulatora</h3>
                    <ul className="space-y-2 text-sm text-gray-300">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                        <span>Obliczenia wytrzymałościowe dla płyt i paneli</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                        <span>Automatyczne znajdowanie optymalnych rozwiązań</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                        <span>Analiza wsparcia i wzmocnień konstrukcyjnych</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                        <span>Wizualizacja 3D ugięć i naprężeń</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-orange-500 mb-3">Współczynniki bezpieczeństwa</h3>
                    <div className="bg-zinc-700 p-4 rounded-lg">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-zinc-600">
                            <th className="text-left pb-2 text-gray-400">Zastosowanie</th>
                            <th className="text-right pb-2 text-gray-400">Współcz.</th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-300">
                          <tr><td>Ekspozycja</td><td className="text-right">2.0</td></tr>
                          <tr><td>Magazynowanie</td><td className="text-right">3.0</td></tr>
                          <tr><td>Konstrukcyjne</td><td className="text-right">4.0</td></tr>
                          <tr><td>Publiczne</td><td className="text-right">5.0</td></tr>
                          <tr><td>Przemysłowe</td><td className="text-right">6.0</td></tr>
                          <tr><td>Krytyczne</td><td className="text-right">10.0</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Footer Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          <div className="bg-zinc-800 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-orange-500">{materialKeys.length}</p>
            <p className="text-sm text-gray-400">Materiałów</p>
          </div>
          <div className="bg-zinc-800 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-blue-500">±0.01mm</p>
            <p className="text-sm text-gray-400">Dokładność</p>
          </div>
          <div className="bg-zinc-800 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-green-500">ISO/DIN</p>
            <p className="text-sm text-gray-400">Normy</p>
          </div>
          <div className="bg-zinc-800 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-purple-500">v2.0</p>
            <p className="text-sm text-gray-400">Wersja</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Eksportuj z ErrorBoundary
import { ErrorBoundary } from '../_devtools/ErrorBoundary';

export const TechnicalCalculator: React.FC = () => {
  return (
    <ErrorBoundary>
      <TechnicalCalculatorInner />
    </ErrorBoundary>
  );
};